import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Check, Loader2, Sparkles, ArrowRight, Users, TrendingUp, AlertCircle,
  Palette, Type, Camera, Target, MapPin, RefreshCw, Home
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import WebsiteRenderer from "@/components/WebsiteRenderer";

const ANALYZE_STEPS = [
  "Finding your business", "Reading the details", "Analyzing customer reviews",
  "Building your brand", "Writing your copy", "Creating your SEO strategy", "Designing your website",
];
const GENERATE_STEPS = ["Home", "About", "Services", "Reviews", "SEO", "FAQ", "Mobile Optimization", "Contact"];

function Checklist({ items, done }) {
  return (
    <div className="flex flex-col gap-3 max-w-sm mx-auto text-left">
      {items.map((label, i) => {
        const active = i <= done;
        const complete = i < done;
        return (
          <motion.div key={label} initial={{ opacity: 0, x: -8 }} animate={{ opacity: active ? 1 : 0.35, x: 0 }} transition={{ delay: i * 0.04 }}
            className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${complete ? "bg-emerald-500" : active ? "ml-ai-btn" : "bg-[#e4e4e7]"}`}>
              {complete ? <Check size={13} color="#fff" /> : active ? <Loader2 size={13} color="#fff" className="animate-spin" /> : null}
            </div>
            <span className={`font-body text-sm ${active ? "text-[#09090b] font-medium" : "text-[#a1a1aa]"}`}>{label}</span>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function Create() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const url = params.get("url") || "";

  const [step, setStep] = useState("analyzing"); // analyzing | insights | verify | generating | preview
  const [project, setProject] = useState(null);
  const [business, setBusiness] = useState(null);
  const [aDone, setADone] = useState(0);
  const [gDone, setGDone] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const started = useRef(false);
  const analyzeTimer = useRef(null);

  const pollProject = async (pid, successStatus, errorStatuses, maxTries = 90) => {
    for (let i = 0; i < maxTries; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/projects/${pid}`);
        if (data.status === successStatus) return data;
        if (errorStatuses.includes(data.status)) throw new Error(data.error || "Something went wrong.");
      } catch (e) {
        if (e.message && !e.response) throw e;
      }
    }
    throw new Error("This is taking longer than expected. Please try again.");
  };

  const runAnalyze = async () => {
    setErrorMsg("");
    setStep("analyzing");
    setADone(0);
    if (analyzeTimer.current) clearInterval(analyzeTimer.current);
    analyzeTimer.current = setInterval(() => setADone((d) => Math.min(d + 1, ANALYZE_STEPS.length - 1)), 5500);
    try {
      const { data: created } = await api.post("/analyze", { maps_url: url });
      const done = await pollProject(created.id, "analyzed", ["error"]);
      setProject(done);
      setBusiness(done.profile.business);
      setADone(ANALYZE_STEPS.length);
      setTimeout(() => setStep("insights"), 700);
    } catch (e) {
      setErrorMsg(apiError(e));
      setStep("error");
    } finally {
      clearInterval(analyzeTimer.current);
    }
  };

  // Analyze
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!url) { nav("/"); return; }
    runAnalyze();
    return () => { if (analyzeTimer.current) clearInterval(analyzeTimer.current); };
  }, [url, nav]); // eslint-disable-line

  const runGenerate = async () => {
    setStep("generating");
    setGDone(0);
    const timer = setInterval(() => setGDone((d) => Math.min(d + 1, GENERATE_STEPS.length - 1)), 4500);
    try {
      await api.put(`/projects/${project.id}/profile`, { profile: { ...project.profile, business } });
      await api.post("/generate", { project_id: project.id });
      const done = await pollProject(project.id, "generated", ["generate_error"]);
      setProject(done);
      setGDone(GENERATE_STEPS.length);
      setTimeout(() => setStep("preview"), 700);
    } catch (e) {
      toast.error(apiError(e));
      setStep("verify");
    } finally {
      clearInterval(timer);
    }
  };

  const openEditor = async () => {
    if (user) {
      try {
        await api.post(`/projects/${project.id}/claim`);
        nav(`/editor/${project.id}`);
      } catch (e) {
        toast.error(apiError(e));
        if (e?.response?.status === 403) nav("/pricing");
      }
    } else {
      nav(`/register?next=${encodeURIComponent(`/editor/${project.id}`)}`);
    }
  };

  const intel = project?.profile?.intelligence || {};
  const brand = project?.profile?.brand || {};

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="ml-label">{step === "preview" ? "Preview" : "Building your site"}</div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* ANALYZING */}
        {step === "analyzing" && (
          <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-2xl mx-auto px-6 py-24 text-center">
            <div className="w-16 h-16 rounded-2xl ml-ai-btn flex items-center justify-center mx-auto mb-8">
              <Sparkles size={28} color="#fff" />
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b]">Analyzing your business…</h1>
            <p className="font-body text-[#52525b] mt-3 mb-12">Our AI is reading your Google presence to craft something great.</p>
            <Checklist items={ANALYZE_STEPS} done={aDone} />
          </motion.div>
        )}

        {/* ERROR */}
        {step === "error" && (
          <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-lg mx-auto px-6 py-28 text-center">
            <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={28} className="text-amber-500" />
            </div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#09090b]">We hit a snag</h1>
            <p className="font-body text-[#52525b] mt-3">{errorMsg || "Something went wrong while analyzing your business."}</p>
            <div className="flex items-center justify-center gap-3 mt-8">
              <button data-testid="analyze-retry-btn" onClick={runAnalyze}
                className="ml-ai-btn rounded-full px-7 py-3.5 font-semibold text-sm inline-flex items-center gap-2 font-body">
                <RefreshCw size={16} /> Try Again
              </button>
              <button data-testid="analyze-home-btn" onClick={() => nav("/")}
                className="border border-border rounded-full px-6 py-3.5 font-semibold text-sm inline-flex items-center gap-2 font-body text-[#52525b]">
                <Home size={16} /> Start Over
              </button>
            </div>
          </motion.div>
        )}

        {/* INSIGHTS */}
        {step === "insights" && business && (
          <motion.div key="insights" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="max-w-5xl mx-auto px-6 sm:px-10 py-16">
            <div className="ml-label mb-2">Analysis complete</div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b]">Here's what we learned.</h1>

            <div className="bg-[#09090b] rounded-2xl p-8 mt-8 text-white">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-heading font-bold text-2xl">{business.name}</div>
                  <div className="font-body text-white/60 mt-1">{business.category} · {business.city}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading font-bold text-2xl text-sky-400">{business.rating}★</div>
                  <div className="font-body text-white/60 text-sm">{business.review_count} reviews</div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <InsightCard icon={Users} title="Ideal Customers" items={intel.ideal_customers} />
              <InsightCard icon={TrendingUp} title="Strengths" items={intel.strengths} />
              <InsightCard icon={AlertCircle} title="Objections to Overcome" items={intel.sentiment?.objections} />
              <InsightCard icon={Target} title="Unique Selling Point" text={intel.usp} />
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <div className="border border-border rounded-2xl p-6">
                <div className="flex items-center gap-2 text-[#09090b] mb-4"><Palette size={16} /><span className="font-heading font-semibold">Brand Palette</span></div>
                <div className="flex gap-2">
                  {["primary", "secondary", "accent"].map((k) => (
                    <div key={k} className="flex-1">
                      <div className="h-12 rounded-lg border border-border" style={{ background: brand.colors?.[k] }} />
                      <div className="font-mono-ml text-[10px] text-[#a1a1aa] mt-1">{brand.colors?.[k]}</div>
                    </div>
                  ))}
                </div>
                <div className="font-body text-sm text-[#52525b] mt-3">{brand.personality}</div>
              </div>
              <div className="border border-border rounded-2xl p-6">
                <div className="flex items-center gap-2 text-[#09090b] mb-4"><Type size={16} /><span className="font-heading font-semibold">Typography</span></div>
                <div className="font-body text-sm text-[#52525b]">Headings: <span className="text-[#09090b] font-semibold">{brand.fonts?.heading}</span></div>
                <div className="font-body text-sm text-[#52525b] mt-1">Body: <span className="text-[#09090b] font-semibold">{brand.fonts?.body}</span></div>
              </div>
              <div className="border border-border rounded-2xl p-6">
                <div className="flex items-center gap-2 text-[#09090b] mb-4"><Camera size={16} /><span className="font-heading font-semibold">Photography</span></div>
                <div className="font-body text-sm text-[#52525b]">{brand.photography_style}</div>
              </div>
            </div>

            <div className="flex justify-end mt-10">
              <button data-testid="insights-continue-btn" onClick={() => setStep("verify")}
                className="bg-[#09090b] text-white rounded-full px-7 py-3.5 font-semibold text-sm inline-flex items-center gap-2 font-body">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* VERIFY */}
        {step === "verify" && business && (
          <motion.div key="verify" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="max-w-2xl mx-auto px-6 py-16">
            <div className="ml-label mb-2">Verify your details</div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#09090b]">Does everything look right?</h1>
            <p className="font-body text-[#52525b] mt-2">We pre-filled everything we could. Edit anything before we build your site.</p>

            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              <Field label="Business Name" value={business.name} onChange={(v) => setBusiness({ ...business, name: v })} testid="verify-name" />
              <Field label="Category" value={business.category} onChange={(v) => setBusiness({ ...business, category: v })} testid="verify-category" />
              <Field label="Phone" value={business.phone} onChange={(v) => setBusiness({ ...business, phone: v })} testid="verify-phone" />
              <Field label="Email" value={business.email} onChange={(v) => setBusiness({ ...business, email: v })} placeholder="Add it now or skip" testid="verify-email" highlight={!business.email} />
              <Field label="Address" value={business.address} onChange={(v) => setBusiness({ ...business, address: v })} testid="verify-address" />
              <Field label="City" value={business.city} onChange={(v) => setBusiness({ ...business, city: v })} testid="verify-city" />
            </div>
            <div className="mt-5">
              <label className="ml-label">Description</label>
              <textarea data-testid="verify-description" value={business.description} onChange={(e) => setBusiness({ ...business, description: e.target.value })}
                rows={3} className="w-full mt-2 border border-border rounded-xl px-4 py-3 font-body text-sm outline-none focus:ring-2 focus:ring-sky-400" />
            </div>

            <div className="flex justify-between items-center mt-10">
              <button onClick={() => setStep("insights")} className="font-body text-sm text-[#52525b] font-medium">← Back</button>
              <button data-testid="verify-generate-btn" onClick={runGenerate}
                className="ml-ai-btn rounded-full px-7 py-3.5 font-semibold text-sm inline-flex items-center gap-2 font-body">
                <Sparkles size={16} /> Generate My Website
              </button>
            </div>
          </motion.div>
        )}

        {/* GENERATING */}
        {step === "generating" && (
          <motion.div key="generating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="max-w-2xl mx-auto px-6 py-24 text-center">
            <div className="w-16 h-16 rounded-2xl ml-ai-btn flex items-center justify-center mx-auto mb-8">
              <Loader2 size={28} color="#fff" className="animate-spin" />
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b]">Designing your website…</h1>
            <p className="font-body text-[#52525b] mt-3 mb-12">Assembling pages tailored to your business.</p>
            <Checklist items={GENERATE_STEPS} done={gDone} />
          </motion.div>
        )}

        {/* PREVIEW */}
        {step === "preview" && project?.website && (
          <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-border">
              <div className="max-w-7xl mx-auto px-6 sm:px-10 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center"><Check size={15} color="#fff" /></div>
                  <span className="font-heading font-semibold text-[#09090b]">Your website is ready.</span>
                  <span className="ml-2 hidden sm:inline font-mono-ml text-xs text-emerald-600">Conversion score {project.website.meta?.conversion_score}/100</span>
                </div>
                <button data-testid="preview-edit-btn" onClick={openEditor}
                  className="ml-ai-btn rounded-full px-6 py-2.5 font-semibold text-sm inline-flex items-center gap-2 font-body">
                  {user ? "Open Editor" : "Save & Edit — Free Account"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
            <div className="max-w-6xl mx-auto p-6">
              <div className="rounded-2xl overflow-hidden border border-border shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)]">
                <WebsiteRenderer website={project.website} profile={project.profile} editable={false} vp="desktop" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InsightCard({ icon: Icon, title, items, text }) {
  return (
    <div className="border border-border rounded-2xl p-6">
      <div className="flex items-center gap-2 text-[#09090b] mb-4"><Icon size={16} /><span className="font-heading font-semibold">{title}</span></div>
      {text && <p className="font-body text-sm text-[#52525b] leading-relaxed">{text}</p>}
      {items && (
        <div className="flex flex-wrap gap-2">
          {items.map((it, i) => (
            <span key={i} className="bg-[#f4f4f5] text-[#09090b] text-xs font-medium px-3 py-1.5 rounded-full font-body">{it}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, highlight, testid }) {
  return (
    <div>
      <label className="ml-label">{label}</label>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`w-full mt-2 border rounded-xl px-4 py-3 font-body text-sm outline-none focus:ring-2 focus:ring-sky-400 ${highlight ? "border-amber-400 bg-amber-50/40" : "border-border"}`} />
    </div>
  );
}
